import React from 'react'
import styles from './TextInput.module.css'

export interface TextInputProps {
  /**
   * Input type
   */
  type: 'text' | 'email' | 'phone'

  /**
   * Placeholder text
   */
  placeholder: string

  /**
   * Callback when user submits input
   */
  onSubmit: (value: string) => void

  /**
   * Optional validation function
   * Returns error message or null if valid
   */
  validate?: (value: string) => string | null

  /**
   * Whether input is disabled
   * @default false
   */
  disabled?: boolean

  /**
   * Primary brand color
   * @default '#0066CC'
   */
  primaryColor?: string
}

/**
 * Text input component for user text entry
 * Supports text, email, and phone input types
 */
export const TextInput: React.FC<TextInputProps> = ({
  type,
  placeholder,
  onSubmit,
  validate,
  disabled = false,
  primaryColor = '#0066CC',
}) => {
  const [value, setValue] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)

  // Auto-focus on mount
  React.useEffect(() => {
    if (inputRef.current && !disabled) {
      inputRef.current.focus()
    }
  }, [disabled])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (disabled || !value.trim()) {
      return
    }

    // Validate if validator provided
    if (validate) {
      const validationError = validate(value)
      if (validationError) {
        setError(validationError)
        return
      }
    }

    // Submit and reset
    setError(null)
    onSubmit(value.trim())
    setValue('')
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
    // Clear error on change
    if (error) {
      setError(null)
    }
  }

  // Determine inputMode for mobile keyboards
  const inputMode =
    type === 'email' ? 'email' : type === 'phone' ? 'tel' : 'text'

  return (
    <form className={styles.textInput} onSubmit={handleSubmit} data-testid="text-input-form">
      <div className={styles.inputWrapper}>
        <input
          ref={inputRef}
          type={type === 'phone' ? 'tel' : type}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          inputMode={inputMode}
          className={styles.input}
          data-testid="text-input"
          aria-label={placeholder}
          aria-invalid={!!error}
          aria-describedby={error ? 'input-error' : undefined}
        />
        <button
          type="submit"
          disabled={disabled || !value.trim()}
          className={styles.submitButton}
          style={{ backgroundColor: primaryColor }}
          data-testid="submit-button"
          aria-label="Send message"
        >
          {/* Send icon (paper plane) */}
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      {error && (
        <p className={styles.errorMessage} id="input-error" data-testid="error-message" role="alert">
          {error}
        </p>
      )}
    </form>
  )
}
